import React from 'react';
import CustomResizeableWindow from '../overwrite/components/CustomResizeableWindow';
import FeatureDetailsView from '../components/FeatureDetailsView';
import '../style/FeatureDetails.css';
import ComponentUtils from "../utils/ComponentUtils";
import TypeUtils from "../utils/TypeUtils";
import LocaleUtils from "qwc2/utils/LocaleUtils";
import {connect} from "react-redux";
import {removeDetailWindow, stepDetailWindow} from "../actions/details";
import {panTo, zoomToExtent} from "qwc2/actions/map";
import {clearEditContext} from "qwc2/actions/editing";
import geojsonBbox from "geojson-bounding-box";
import PropTypes from "prop-types";
import FeatureUtils from "../utils/FeatureUtils";

class FeatureDetail extends React.Component {
    static propTypes = {
        clearEditContext: PropTypes.func,
        geomEditActive: PropTypes.bool,
        geomEditContext: PropTypes.object,
        isDirty: PropTypes.bool,
        panTo: PropTypes.func,
        removeDetailWindow: PropTypes.func,
        stepDetailWindow: PropTypes.func,
        zoomToExtent: PropTypes.func
    }

    constructor(props) {
        super(props);

        this.state = {
            wcfg: null,
            loading: true,
            error: null
        };

        this._isMounted = false;
    }

    async componentDidMount() {
        this._isMounted = true;
        await this.loadConfig(this.props);
    }

    async componentDidUpdate(prevProps) {
        if (
            prevProps.window.id !== this.props.window.id ||
            JSON.stringify(prevProps.window.feature) !== JSON.stringify(this.props.window.feature)
        ) {
            await this.loadConfig(this.props);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.props.geomEditActive) {
            this.props.clearEditContext(this.getGeomEditContextId());
        }
    }

    getGeomEditContextId() {
        return 'geomedit-' + this.props.window.id;
    }

    // the feature to show/highlight/pan-zoom-to: the saved one, unless a geometry edit is in progress,
    // in which case the not-yet-saved position should be reflected everywhere the feature is displayed
    getDisplayFeature() {
        const feature = this.props.window.feature;
        const context = this.props.geomEditContext;
        if (context && context.changed && context.feature && context.feature.geometry) {
            if (this._displayFeatureSource !== feature || this._displayFeatureContext !== context) {
                this._displayFeatureSource = feature;
                this._displayFeatureContext = context;
                this._displayFeature = {...feature, geometry: context.feature.geometry};
            }
            return this._displayFeature;
        }
        return feature;
    }

    async loadConfig(props) {
        const {window} = props;

        this.setState({
            loading: true,
            error: null
        });

        try {
            const wcfg = await this.getWindowConfig(window.feature);

            if (!this._isMounted) {
                return;
            }

            this.setState({
                wcfg,
                loading: false,
                error: null
            });
        } catch (error) {
            if (!this._isMounted) {
                return;
            }

            this.setState({
                wcfg: null,
                loading: false,
                error
            });
        }
    }

    async getWindowConfig(feature) {
        const objectType = FeatureUtils.getObjectType(feature);

        if (feature.detailWindowType && feature.detailWindowType === 'component') {
            return {
                icon: 'tools',
                label: await ComponentUtils.getObjectLabel(feature),
                tabConfig: await ComponentUtils.getTabConfig(feature),
                breadcrumbUrl: ComponentUtils.getBreadcrumbUrl(feature)
            };
        }
        if (feature.detailWindowType && feature.detailWindowType === 'newFeature') {
            return {
                icon: 'addfeature',
                label: LocaleUtils.tr("featuredetails.newfeaturetitle", feature.newFeatureLabel || objectType),
                tabConfig: {
                    type: objectType,
                    tabs: [{
                        label: LocaleUtils.trmsg("featuredetails.newfeaturetab"),
                        view: 'featureform',
                        roles: feature.newFeatureRoles
                    }]
                },
                breadcrumbUrl: null
            };
        }

        const tabConfig = TypeUtils.getTypeConfig(objectType);
        return {
            icon: 'info-sign',
            label: TypeUtils.getObjectLabel(objectType, feature),
            tabConfig: tabConfig,
            breadcrumbUrl: TypeUtils.getBreadcrumbUrl(feature, tabConfig.type)
        };
    }

    getExtraControls() {
        const feature = this.getDisplayFeature();
        const {window} = this.props;
        const extraControls = [
            {
                icon: "chevron-left",
                disabled: window.past.length === 0,
                titlemsgid: LocaleUtils.trmsg("featuredetails.back"),
                callback: () => this.props.stepDetailWindow(window.id, -1)
            },
            {
                icon: "chevron-right",
                disabled: window.future.length === 0,
                titlemsgid: LocaleUtils.trmsg("featuredetails.forward"),
                callback: () => this.props.stepDetailWindow(window.id, 1)
            }
        ];

        if (feature.geometry || feature.properties?.geometry) {
            extraControls.push({
                icon: "arrows",
                callback: () => this.panTo(feature)
            });
            extraControls.push({
                icon: "zoom",
                callback: () => this.zoomTo(feature)
            });
        }

        return extraControls;
    }

    renderBody() {
        const {window} = this.props;
        const {wcfg, loading, error} = this.state;

        if (loading) {
            return <div>...</div>;
        }

        if (error || !wcfg) {
            return <div>Could not load window configuration.</div>;
        }

        return (
            <FeatureDetailsView
                breadcrumbUrl={wcfg.breadcrumbUrl}
                feature={window.feature}
                initialTab={window.tab}
                key={window.feature.properties?.id}
                tabs={wcfg.tabConfig.tabs}
                type={wcfg.tabConfig.type}
                view={wcfg.tabConfig.view}
                windowId={window.id}
                windowType={window.feature.detailWindowType}
            />
        );
    }

    onClose = () => {
        if (this.props.isDirty && !window.confirm(LocaleUtils.tr("featuredetails.unsavedchanges"))) {
            return;
        }
        this.props.removeDetailWindow(this.props.window);
    }

    render() {
        const {windowSize} = this.props;
        const {wcfg} = this.state;

        return (
            <CustomResizeableWindow
                extraControls={wcfg ? this.getExtraControls() : []}
                feature={this.getDisplayFeature()}
                icon={wcfg ? wcfg.icon : "spinner"}
                initialHeight={windowSize.height}
                initialWidth={windowSize.width}
                onClose={this.onClose}
                scrollable={true}
                title={wcfg ? wcfg.label : "Loading..."}
            >
                <div className="service-info-window-body" role="body">
                    {this.renderBody()}
                </div>
            </CustomResizeableWindow>
        );
    }

    panTo(feature) {
        let bounds;
        let crs = null;
        if (feature.properties.geometry) {
            bounds = feature.properties.geometry.getExtent();
        } else {
            bounds = geojsonBbox(feature);
            crs = 'EPSG:4326';
        }
        const center = [0.5 * (bounds[0] + bounds[2]), 0.5 * (bounds[1] + bounds[3])];
        this.props.panTo(center, crs);
    }

    zoomTo(feature) {
        let extent;
        if (feature.properties.geometry) {
            extent = feature.properties.geometry.getExtent();
            this.props.zoomToExtent(extent, null, -1);
        } else {
            extent = geojsonBbox(feature);
            this.props.zoomToExtent(extent, 'EPSG:4326', -1);
        }
    }
}

const selector = (state, ownProps) => ({
    isDirty: !!state.dirty[ownProps.window.id],
    geomEditActive: state.editing.currentContext === ('geomedit-' + ownProps.window.id),
    geomEditContext: state.editing.contexts['geomedit-' + ownProps.window.id]
});

export default connect(selector, {
    removeDetailWindow: removeDetailWindow,
    stepDetailWindow: stepDetailWindow,
    panTo: panTo,
    zoomToExtent: zoomToExtent,
    clearEditContext: clearEditContext
})(FeatureDetail);
